import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  Plus, Trash2, Bell, BellRing, Clock, Repeat, CheckCircle2, Calendar, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const recurrenceLabels = {
  null: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly'
};

const ReminderDialog = ({ open, onOpenChange, channelId, dmId }) => {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  const [creating, setCreating] = useState(false);

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (channelId) params.set('channel_id', channelId);
      if (dmId) params.set('dm_id', dmId);
      const res = await axios.get(`${API}/reminders?${params}`);
      setReminders(res.data.reminders || []);
    } catch (e) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, [channelId, dmId]);

  useEffect(() => {
    if (open) loadReminders();
  }, [open, loadReminders]);

  const handleCreate = async () => {
    if (!title.trim() || !remindAt) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/reminders`, {
        title: title.trim(),
        notes: notes.trim(),
        remind_at: new Date(remindAt).toISOString(),
        recurrence: recurrence === 'none' ? null : recurrence,
        channel_id: channelId || null,
        dm_id: dmId || null
      });
      setReminders(prev => [res.data.reminder, ...prev].sort((a, b) => new Date(a.remind_at) - new Date(b.remind_at)));
      setTitle('');
      setNotes('');
      setRemindAt('');
      setRecurrence('none');
      setShowCreate(false);
      toast.success('Reminder created');
    } catch (e) {
      toast.error('Failed to create reminder');
    } finally {
      setCreating(false);
    }
  };

  const handleComplete = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}`, { status: 'completed' });
      setReminders(prev => prev.map(r => r.reminder_id === reminderId ? { ...r, status: 'completed' } : r));
      toast.success('Reminder completed');
    } catch (e) {
      toast.error('Failed to update reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      setReminders(prev => prev.filter(r => r.reminder_id !== reminderId));
      toast.success('Reminder deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const isPast = (iso) => new Date(iso) < new Date();
  const activeReminders = reminders.filter(r => r.status === 'active');
  const completedReminders = reminders.filter(r => r.status === 'completed');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[80vh] flex flex-col" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Bell className="w-5 h-5 text-[hsl(var(--primary))]" />
            Reminders
          </DialogTitle>
        </DialogHeader>

        {!showCreate ? (
          <>
            <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => setShowCreate(true)} data-testid="new-reminder-btn">
              <Plus className="w-3.5 h-3.5" /> New Reminder
            </Button>

            <ScrollArea className="flex-1 max-h-[400px]">
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="space-y-3">
                  {/* Active */}
                  {activeReminders.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Active</p>
                      <div className="space-y-1">
                        {activeReminders.map(r => (
                          <div
                            key={r.reminder_id}
                            className={`flex items-start gap-3 p-3 rounded-lg border border-border transition-colors ${isPast(r.remind_at) ? 'bg-amber-50 border-amber-200' : ''}`}
                            data-testid={`reminder-item-${r.reminder_id}`}
                          >
                            <button onClick={() => handleComplete(r.reminder_id)} className="mt-0.5 flex-shrink-0" data-testid={`complete-reminder-${r.reminder_id}`}>
                              <BellRing className={`w-5 h-5 ${isPast(r.remind_at) ? 'text-amber-500' : 'text-[hsl(var(--primary))]'}`} />
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{r.title}</p>
                              {r.notes && <p className="text-xs text-muted-foreground mt-0.5">{r.notes}</p>}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDateTime(r.remind_at)}
                                </span>
                                {r.recurrence && (
                                  <Badge variant="secondary" className="text-[10px] h-4 gap-0.5">
                                    <Repeat className="w-2.5 h-2.5" />
                                    {recurrenceLabels[r.recurrence]}
                                  </Badge>
                                )}
                                {isPast(r.remind_at) && (
                                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-4">Overdue</Badge>
                                )}
                              </div>
                            </div>
                            {r.created_by === user?.user_id && (
                              <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => handleDelete(r.reminder_id)} data-testid={`delete-reminder-${r.reminder_id}`}>
                                <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Completed */}
                  {completedReminders.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Completed</p>
                      <div className="space-y-1">
                        {completedReminders.map(r => (
                          <div key={r.reminder_id} className="flex items-center gap-3 p-3 rounded-lg border border-border opacity-50" data-testid={`completed-reminder-${r.reminder_id}`}>
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium line-through">{r.title}</p>
                              <span className="text-[11px] text-muted-foreground">{formatDateTime(r.remind_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {reminders.length === 0 && (
                    <div className="text-center py-8">
                      <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                      <p className="text-sm text-muted-foreground">No reminders yet</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Create one to stay on track</p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <>
            {/* Create form */}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Remind me about..."
                  className="h-9 text-sm"
                  data-testid="reminder-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes (optional)</label>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Additional details..."
                  className="text-sm resize-none"
                  rows={2}
                  data-testid="reminder-notes-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">When</label>
                <Input
                  type="datetime-local"
                  value={remindAt}
                  onChange={e => setRemindAt(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="reminder-datetime-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Repeat</label>
                <Select value={recurrence} onValueChange={setRecurrence}>
                  <SelectTrigger className="h-9 text-sm" data-testid="reminder-recurrence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">One-time</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} data-testid="cancel-reminder-btn">Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={!title.trim() || !remindAt || creating} data-testid="save-reminder-btn">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Bell className="w-3.5 h-3.5 mr-1" />}
                Create Reminder
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReminderDialog;
